
export interface AppGenerationResult {
  appName: string;
  tagline: string;
  features: string[];
  suggestedColorPalette: string[];
  logicWorkflow: string;
  techStack: string[];
  targetAudience: string;
  monetization: string;
  mvpScope: string[];
  competitiveEdge: string;
  userPersonas: { name: string; role: string; painPoint: string }[];
}

export enum Section {
  HERO = 'hero',
  BUILDER = 'builder',
  FEATURES = 'features'
}

export enum View {
  LANDING = 'landing',
  AUTH = 'auth',
  DASHBOARD = 'dashboard',
  LUMI = 'lumi',
  PRICING = 'pricing',
  DOCUMENTATION = 'documentation',
  DEMO = 'demo',
  GENERATOR = 'generator',
  EXTENSIONS = 'extensions',
  PRIVACY = 'privacy',
  TERMS = 'terms',
  LEGAL = 'legal',
  CONTACT = 'contact',
  COMMANDS = 'commands',
  FEATURES = 'features',
  HOW_IT_WORKS = 'how-it-works',
  HIGGSFIELD = 'higgsfield',
  WORKCLOCK = 'workclock',
  TEAM_DASHBOARD = 'team-dashboard',
  STUDIO = 'studio',
  COMMUNITY = 'community',
  NOT_FOUND = 'not-found',
  BLOG = 'blog',
  BLOG_POST = 'blog-post',
  COMPARISON = 'comparison',
  LEETCODE = 'leetcode',
}

// ============ Platform Types ============

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type Urgency = 'low' | 'medium' | 'high' | 'critical';

export interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  plan: 'free' | 'pro' | 'proPlus' | 'enterprise';
  createdAt: string;
}

export interface DiagnosisResult {
  problemSummary: string;
  likelyCauses: string[];
  explanation: string;
  urgency: Urgency;
  canAutoFix: boolean;
}

export interface FixStep {
  step: number;
  action: string;
  details: string;
  expectedResult: string;
}

export interface PRReview {
  summary: string;
  approved: boolean;
  comments: string[];
  suggestions: string[];
}

export interface TestResult {
  tests: string;
  coverage: {
    functions: string[];
    edgeCases: string[];
    notCovered: string[];
  };
  explanation: string;
}

export interface LegalAnalysis {
  documentType: string;
  summary: string;
  keyTerms: Array<{ term: string; explanation: string }>;
  obligations: string[];
  rights: string[];
  redFlags: Array<{ clause: string; risk: RiskLevel; explanation: string }>;
  overallRisk: RiskLevel;
}

export interface WeeklyBriefing {
  executiveSummary: string;
  keyMovements: Array<{
    competitor: string;
    movement: string;
    significance: Urgency;
  }>;
  threats: string[];
  opportunities: string[];
  recommendations: Array<{
    action: string;
    priority: string;
    rationale: string;
  }>;
}

export interface KnowledgeEntry {
  id: string;
  source: 'slack' | 'docs' | 'code' | 'meetings' | 'decisions' | 'email';
  ingested: string;
  summary?: string;
  tags?: string[];
  author?: string;
  team?: string;
}

export interface QueryResult {
  answer: string;
  sources: Array<{ id: string; relevance: number }>;
  relatedTopics: string[];
  suggestedFollowUp: string[];
  confidence: number;
}

export interface DashboardStats {
  totalAnalyses: number;
  testsGenerated: number;
  documentsReviewed: number;
  knowledgeEntries: number;
  competitorsTracked: number;
}

export interface GoalPlanPhase {
  name: string;
  duration: string;
  owner: string;
  deliverables: string[];
  validation: string;
}

export interface GoalPlanExperiment {
  name: string;
  hypothesis: string;
  metric: string;
  target: string;
  guardrail: string;
  rollout: string;
}

export interface GoalPlanRisk {
  risk: string;
  mitigation: string;
  severity: 'low' | 'medium' | 'high';
}

export interface GoalPlan {
  planName: string;
  goal: string;
  northStarMetric: string;
  targetImpact: string;
  assumptions: string[];
  phases: GoalPlanPhase[];
  experiments: GoalPlanExperiment[];
  instrumentation: Array<{
    event: string;
    purpose: string;
    owner: string;
  }>;
  risks: GoalPlanRisk[];
  nextSevenDays: string[];
}

export interface GoalPlanInput {
  goal: string;
  product?: string;
  audience?: string;
  constraints?: string;
  baseline?: string;
  timeframeWeeks?: number;
}

export interface StoredGoalPlan {
  id: string;
  goal: string;
  createdAt: string;
  updatedAt: string;
  input: GoalPlanInput;
  plan: GoalPlan;
}

export interface ExecutionTicket {
  id: string;
  title: string;
  owner: string;
  priority: 'P0' | 'P1' | 'P2';
  estimateDays: number;
  objective: string;
  deliverables: string[];
  acceptanceCriteria: string[];
  dependencies: string[];
  rolloutStep: string;
}

export interface ExecutionPlan {
  summary: string;
  sprintCadence: string;
  tickets: ExecutionTicket[];
  qaGates: string[];
  launchChecklist: string[];
}

export interface ExecutionPlanInput {
  teamContext?: string;
  engineersCount?: number;
  sprintLengthDays?: number;
}

export interface StoredExecutionPlan {
  id: string;
  planId: string;
  createdAt: string;
  updatedAt: string;
  input: ExecutionPlanInput;
  execution: ExecutionPlan;
}

export type ExecutionTicketStatus = 'todo' | 'in_progress' | 'blocked' | 'done';

export interface ExternalIssueLink {
  id: string;
  provider: 'github' | 'jira';
  externalId: string;
  url: string;
  title: string;
  status: 'open' | 'closed';
  createdAt: string;
}

export interface ExecutionTicketProgress {
  ticketId: string;
  status: ExecutionTicketStatus;
  updatedAt: string;
  note?: string;
  externalIssues: ExternalIssueLink[];
}

export interface ExecutionProgressSnapshot {
  executionId: string;
  planId: string;
  createdAt: string;
  updatedAt: string;
  ticketProgress: ExecutionTicketProgress[];
  totals: {
    totalTickets: number;
    todo: number;
    inProgress: number;
    blocked: number;
    done: number;
    completionPercent: number;
    estimatedDaysTotal: number;
    estimatedDaysRemaining: number;
  };
  readiness: {
    qaGateIndexesDone: number[];
    checklistIndexesDone: number[];
    qaGateCompletionPercent: number;
    checklistCompletionPercent: number;
    rolloutReadinessPercent: number;
    summary: string;
  };
  burnDown: Array<{
    day: number;
    targetRemaining: number;
    actualRemaining: number;
  }>;
}

// ============ Studio Types ============

export interface PageBlueprint {
  id: string;
  name: string;
  route: string;
  purpose: string;
  sections: string[];
}

export interface ComponentBlueprint {
  id: string;
  name: string;
  type: string;
  props: string[];
  description: string;
}

export interface ApiBlueprint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  description: string;
  requestBody?: string;
  responseBody?: string;
}

export interface DatabaseModelBlueprint {
  id: string;
  name: string;
  fields: Array<{ name: string; type: string; required: boolean; unique?: boolean; default?: string; relation?: string }>;
  relations: string[];
  indexes?: string[];
  seedRows?: number;
  owner?: string;
}

export interface StudioSuggestion {
  id: string;
  title: string;
  category: 'database' | 'backend' | 'frontend' | 'security' | 'deployment' | 'growth';
  priority: 'critical' | 'high' | 'medium' | 'low';
  impact: string;
  action: string;
  files: string[];
  effort: string;
}

export interface WorkflowBlueprint {
  id: string;
  name: string;
  steps: string[];
  trigger: string;
}

export interface GeneratedFile {
  path: string;
  content: string;
  language: string;
}

export interface ExecutionStep {
  id: string;
  label: string;
  description: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  dependsOn: string[];
  artifact?: string;
}

export interface StudioBlueprint {
  name: string;
  description: string;
  tagline: string;
  targetUsers: string[];
  pages: PageBlueprint[];
  components: ComponentBlueprint[];
  apiEndpoints: ApiBlueprint[];
  databaseModels: DatabaseModelBlueprint[];
  workflows: WorkflowBlueprint[];
  techStack: string[];
  colorPalette: string[];
  features: string[];
  mvpScope: string[];
  monetization: string;
  competitiveEdge: string;
  executionSteps: ExecutionStep[];
  files: GeneratedFile[];
  suggestions?: StudioSuggestion[];
}

export interface StudioProject {
  id: string;
  name: string;
  description: string;
  blueprint: StudioBlueprint;
  createdAt: string;
  updatedAt: string;
}
